import { LEAVES_BG_XS, LEAVES_BG_MD } from '../../config/backgrounds';
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import blogPostService from '../../services/blogPostService';
import { CalendarToday, Person, Label, ArrowBack } from '@mui/icons-material';

const BlogPostDetailPage = () => {
  const theme = useTheme();
  const { slug } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await blogPostService.getBlogPostByIdOrSlug(slug);
        if (response.success) {
          setBlogPost(response.data);
        } else {
          setError(response.message || 'Blogbejegyzés betöltése sikertelen.');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Hiba történt a blogbejegyzés betöltése során.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  const pageBackground = {
    minHeight: '100vh',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundImage: {
        xs: LEAVES_BG_XS,
        md: LEAVES_BG_MD,
      },
      backgroundSize: { xs: '100% auto', md: 'cover' },
      backgroundPosition: { xs: 'center top', md: 'center center' },
      backgroundRepeat: 'no-repeat',
      zIndex: -1,
      willChange: 'transform',
      transform: 'translateZ(0)',
    },
  };

  if (loading) {
    return (
      <Box sx={pageBackground}>
        <Container maxWidth="md" sx={{ pt: 8, textAlign: 'center' }}>
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2, color: 'primary.dark' }}>Betöltés...</Typography>
        </Container>
      </Box>
    );
  }

  if (error || !blogPost) {
    return (
      <Box sx={pageBackground}>
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity={error ? 'error' : 'info'} sx={{ borderRadius: '12px' }}>
            {error || 'A keresett blogbejegyzés nem található.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${blogPost.title} | Wedigo Blog`}</title>
        <meta name="description" content={blogPost.metaDescription || blogPost.title} />
        <meta property="og:title" content={`${blogPost.title} | Wedigo Blog`} />
        <meta property="og:description" content={blogPost.metaDescription || blogPost.title} />
        <meta property="og:url" content={`https://wedigo.hu/blog/${blogPost.slug}`} />
        <meta property="og:type" content="article" />
        {blogPost.imageUrl && <meta property="og:image" content={blogPost.imageUrl} />}
        <link rel="canonical" href={`https://wedigo.hu/blog/${blogPost.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": blogPost.title,
          "description": blogPost.metaDescription || undefined,
          "image": blogPost.imageUrl || undefined,
          "datePublished": blogPost.publishedAt || blogPost.createdAt,
          "dateModified": blogPost.updatedAt,
          "url": `https://wedigo.hu/blog/${blogPost.slug}`,
          "publisher": { "@type": "Organization", "name": "Wedigo", "url": "https://wedigo.hu" },
        })}</script>
      </Helmet>

      <Box sx={pageBackground}>
        <Container maxWidth="md" sx={{ pt: 3, pb: 6 }}>

          {/* Vissza link */}
          <Box
            component={RouterLink}
            to="/blog"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              mb: 2, color: 'primary.main', fontWeight: 500,
              textDecoration: 'none',
              '&:hover': { color: 'primary.dark' },
            }}
          >
            <ArrowBack fontSize="small" />
            <Typography variant="body2" fontWeight={500}>Vissza a bloghoz</Typography>
          </Box>

          {/* Tartalom Paper */}
          <Paper elevation={0} sx={{
            borderRadius: '20px',
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(12px)',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.20)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.10)}`,
            overflow: 'hidden',
          }}>
            {/* Borítókép */}
            {blogPost.imageUrl && (
              <Box
                component="img"
                src={blogPost.imageUrl}
                alt={blogPost.title}
                sx={{
                  width: '100%',
                  height: { xs: 220, sm: 320, md: 400 },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}

            {/* Szöveg tartalom */}
            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
              <Typography variant="h3" component="h1" sx={{ color: 'primary.dark', mb: 2, lineHeight: 1.3 }}>
                {blogPost.title}
              </Typography>

              {/* Meta sor */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2, color: 'text.secondary' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday fontSize="small" />
                  <Typography variant="body2">
                    {format(parseISO(blogPost.publishedAt), 'yyyy. MMMM dd.', { locale: hu })}
                  </Typography>
                </Box>
              </Box>

              {/* Tag-ek */}
              {blogPost.tags && blogPost.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
                  {blogPost.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      icon={<Label fontSize="small" />}
                      sx={{
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.secondary.main, 0.10),
                        color: 'primary.dark',
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                        '& .MuiChip-icon': { color: 'secondary.main' },
                      }}
                    />
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.divider, 0.6) }} />

              {/* Cikk szövege */}
              <Typography
                variant="body1"
                component="div"
                sx={{
                  lineHeight: 1.9,
                  color: 'text.primary',
                  '& h1, & h2, & h3, & h4': { color: 'primary.dark', mt: 3, mb: 1 },
                  '& p': { mb: 1.5 },
                  '& a': { color: 'primary.main' },
                  '& img': { maxWidth: '100%', borderRadius: '12px' },
                  '& blockquote': {
                    borderLeft: `4px solid ${theme.palette.secondary.main}`,
                    pl: 2, ml: 0,
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  },
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blogPost.content) }} />
              </Typography>
            </Box>
          </Paper>

        </Container>
      </Box>
    </>
  );
};

export default BlogPostDetailPage;
